/**
 * Popover primitive — shadcn template wrapping @radix-ui/react-popover.
 *
 * Per G1 verdict (head-web 2026-05-31): Option A — Radix Popover как 21st
 * primitive в `frontend-vite/src/components/ui/`. ARIA-correct для overlay'ев
 * с form controls (Switch). NOT dropdown-menu (role="menuitem" anti-pattern
 * для не-menu items).
 *
 * Match style существующих 20 primitives:
 *   - export Root + Trigger + Anchor (Radix direct re-exports)
 *   - PopoverContent: forwardRef + cn() + Portal внутри + bg-popover token
 *   - className tokens: z-50, rounded-md, border, bg-popover, p-4, shadow-md
 *
 * Typical usage:
 *   <Popover>
 *     <PopoverTrigger asChild><Button variant="ghost">Settings</Button></PopoverTrigger>
 *     <PopoverContent align="end">
 *       <div className="grid gap-4">...</div>
 *     </PopoverContent>
 *   </Popover>
 */
import * as React from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { cn } from '@/lib/utils';

export const Popover = PopoverPrimitive.Root;
export const PopoverTrigger = PopoverPrimitive.Trigger;
export const PopoverAnchor = PopoverPrimitive.Anchor;
export const PopoverClose = PopoverPrimitive.Close;

export const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(({ className, align = 'center', sideOffset = 4, ...props }, ref) => (
  <PopoverPrimitive.Portal>
    <PopoverPrimitive.Content
      ref={ref}
      align={align}
      sideOffset={sideOffset}
      className={cn(
        // Tokens: bg-popover/text-popover-foreground/border + shadow-md (Earth Tech ramp via tokens.css).
        // z-50 — выше map overlay'ев и AntD legacy; Radix Portal mounts в body, не конфликтует с MapLibre canvas z-index.
        'z-50 w-72 rounded-md border border-border bg-popover p-4 text-popover-foreground shadow-md outline-none',
        // Animation: open/close (default Radix data-state attributes + Tailwind animate utility classes).
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
        'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
        className,
      )}
      {...props}
    />
  </PopoverPrimitive.Portal>
));
PopoverContent.displayName = PopoverPrimitive.Content.displayName;
